/**
 * What a refused write shows the user (shipcast-nad4).
 *
 * A write the server refuses because the data changed under it — a save whose
 * If-Match is stale (412), a move whose row or anchor is gone (409) — cannot
 * be fixed by trying again: the screen is behind. That opens SimulationForm's
 * ConflictDialog, whose Refresh loads the current simulation. Every other
 * failure stays a toast.
 */

import { createContext, useContext, useMemo } from 'react';
import { ApiError } from '@/lib/api';
import type { ResourceType } from './useConflictHandler';

/** Opens the conflict dialog for `resourceType`. Provided by SimulationForm. */
export const ConflictContext = createContext<((resourceType: ResourceType, error: ApiError) => void) | null>(null);

export interface WriteErrorHandlers {
  /** For a save (PATCH): a 412 is a conflict. */
  onSaveError: (error: unknown) => void;
  /** For a move: a 409 (row or anchor gone) or 412 is a conflict. */
  onMoveError: (error: unknown) => void;
}

/** Error handlers for this form's writes to `resourceType`; non-conflicts go to `handleError`. */
export function useWriteErrors(resourceType: ResourceType, handleError: (error: unknown) => void): WriteErrorHandlers {
  const openConflict = useContext(ConflictContext);
  return useMemo(() => {
    const handle = (isConflict: (error: ApiError) => boolean) => (error: unknown) => {
      if (openConflict && error instanceof ApiError && isConflict(error)) openConflict(resourceType, error);
      else handleError(error);
    };
    return {
      onSaveError: handle((error) => error.isConflict()),
      onMoveError: handle((error) => error.statusCode === 409 || error.isConflict()),
    };
  }, [openConflict, resourceType, handleError]);
}
